import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./SinglePlaceFormArea.module.css";
import commonStyles from "./addPlaceCommon.module.css";
import ContentTitle from "../ContentTitle";
import { DataTemplate } from "./AddSinglePlaceInput";
import AddCategory from "./AddCategory";
import CategoryList from "./CategoryList";
import PlaceForm from "./PlaceForm";
import { Field, PlaceData, MetaData, CategoryOptions, PlaceCoordsList, Category } from "../../../common/Common";
import DataProvider from "../../../../data_provider/DataProvider";
import { ButtonProps, MiniButtonType } from "../../MiniButton";
import BottomButtonBox from "../../BottomButtonBox";

import { OpenModalParams, useModal } from "../../../modal/Modal";
import ModalButtons from "../../../modal/ModalButtons";

import { ToastData, ToastType } from "../../../common/Toast";
import { MapToolType } from "../../../../screens/MapScreen";
import { AccountManager } from "../../../../workers/AccountManager";

interface EditData extends MetaData {
    value?: { [key: string]: string };
}

export interface MarkerStyles {
    type: string;
    markers: {
        name: string;
    };
}

export const enum SubContentList {
    categoryList,
    addCategory,
}

interface SinglePlaceFormAreaProps {
    categories: CategoryOptions[];
    closeForm: (idx?: number) => void;
    coords: PlaceCoordsList[];
    setCoords: Dispatch<SetStateAction<PlaceCoordsList[]>>;
    setNewPlace?: Dispatch<SetStateAction<PlaceData | undefined>>;
    toggleAddMode: Dispatch<SetStateAction<boolean>>;
    XID?: number | string;
    setToast?: Dispatch<SetStateAction<ToastData | undefined>>;
    setEditPlaceStatus?: Dispatch<SetStateAction<boolean>>;
    newPlaceAddress: string;
    setNewPlaceAddress: Dispatch<SetStateAction<string>>;

    agreeNoneAddress?: boolean;
    setAgreeNoneAddress?: Dispatch<SetStateAction<boolean>>;

    revGeoLoading: boolean;
    setActiveIdx: (idx: number) => void;
    customModal?: { openModal: (param: OpenModalParams) => void; closeModal: () => void };
}

/**
 * @author bakim
 * @date 2024-10-07 11:14:25
 * @remark 단일 장소 등록/수정 화면
 * @param props SinglePlaceFormAreaProps
 * @props
 * @return <SinglePlaceFormArea>
 */
const SinglePlaceFormArea = (props: SinglePlaceFormAreaProps) => {
    const { ModalComponent, openModal, closeModal } = useModal();

    const [data, setData] = useState<PlaceData>();
    const [categories, setCategories] = useState<CategoryOptions[]>(
        //     props.categories[0].label === "전체" ? [...props.categories.slice(1)] : props.categories

        // 251125 | smyun | categories에 값이 없을 때 처리
        Array.isArray(props.categories) && props.categories.length
            ? props.categories[0].label === "전체"
                ? [...props.categories.slice(1)]
                : props.categories
            : []
    );
    const [valid, setValid] = useState<boolean>(false);
    const [formDataChanged, setFormDataChanged] = useState<boolean>(false);
    const [subContent, setSubContent] = useState<SubContentList | undefined>();

    const [params] = useSearchParams();
    const formRef = useRef<HTMLFormElement>(null);
    const mapid = params.get("id");

    const showModal = (message: string) => {
        openModal({
            title: "안내",
            children: message,
            modalButtons: (
                <ModalButtons
                    buttonCount={1}
                    buttons={[
                        {
                            label: "확인",
                            color: "Primary",
                            onClick: () => closeModal(),
                        },
                    ]}
                />
            ),
        });
    };

    useEffect(() => {
        getMapData();
    }, []);

    useEffect(() => {
        if (props.XID && typeof props.XID === "string" && props.setNewPlace) {
            props.setNewPlace(data);
        }
    }, [props.XID]);

    const cancel = () => {
        props.closeForm(Number(props.XID));
        props.toggleAddMode(false);
        props.setEditPlaceStatus && props.setEditPlaceStatus(true);
        props.setNewPlaceAddress(""); // 지도 변경주소 초기화
    };

    const onSubmit = async (e?: React.MouseEvent<HTMLElement>) => {
        e?.preventDefault();
        if (!formRef.current || !valid || !mapid) return;
        const formData = new FormData(formRef.current);

        // 추가할 이미지가 있는 경우 이미지 이름 데이터 추가
        formData.getAll("images").forEach((file) => {
            file instanceof File && formData.append("imageNames", file.name);
        });

        let targetXID = undefined;

        if (data && data.XID) {
            if (formData.has("geometry") && !formData.get("geometry")) {
                formData.delete("geometry");
            }

            const response = await DataProvider.UpdatePlaceInfo({ mapid, XID: data.XID.toString() }, formData);

            // 카테고리 추가됐을 경우
            const updateCategory = {
                category: categories
                    .filter((category) => category.disabled !== undefined && !category.disabled)
                    .map((category) => ({
                        ...category,
                        icon: category.icon,
                        label: category.label,
                        color: category.color,
                    })) as Category[],
            };
            const updateCategoryResponse = await DataProvider.UpdateMapInfo({ mapid }, updateCategory);

            if (response.flag && updateCategoryResponse.flag) {
                targetXID = data.XID;
                props.toggleAddMode(false);
                props.setEditPlaceStatus && props.setEditPlaceStatus(true); // 마커 새로고침
                props.setToast && props.setToast({ type: ToastType.DEFAULT, message: "수정이 완료되었습니다.", iconType: "addTask" });
            } else {
                switch (response.status) {
                    case 403:
                        showModal("장소 수정 권한이 없습니다.");
                        props.closeForm(data.XID);
                        break;
                    case 404:
                        showModal("장소를 찾을 수 없습니다.");
                        props.closeForm();
                        break;
                    default:
                        showModal("장소 수정에 실패하였습니다.");
                }

                console.error(response);
            }
        } else {
            // formData.forEach((value, key) => {
            //     console.log(key, value);
            // });
            const response = await DataProvider.AddPlaceInfo({ mapid }, formData);

            // 카테고리 추가됐을 경우
            const updateCategory = {
                category: categories
                    .filter((category) => category.disabled !== undefined && !category.disabled)
                    .map((category) => ({
                        ...category,
                        icon: category.icon,
                        label: category.label,
                        color: category.color,
                    })) as Category[],
            };
            const updateCategoryResponse = await DataProvider.UpdateMapInfo({ mapid }, updateCategory);

            if (response.flag && updateCategoryResponse.flag) {
                targetXID = response.result.XID;
                props.toggleAddMode(false);
                props.setEditPlaceStatus && props.setEditPlaceStatus(true); // 마커 새로고침
                props.setToast && props.setToast({ type: ToastType.DEFAULT, message: "장소 추가가 완료되었습니다.", iconType: "addTask" });
            } else {
                // TODO: 에러 페이지 처리 필요(500, 404)
                switch (response.status) {
                    case 204:
                        showModal("장소 정보를 찾을 수 없습니다.");
                        break;
                    case 403:
                        showModal("장소 추가 권한이 없습니다.");
                        break;
                    case 404:
                        showModal("삭제되었거나 존재하지 않는 장소입니다.");
                        break;
                    case 500:
                        showModal("장소 정보를 불러오는 중 서버 오류가 발생했습니다.");
                        break;
                    default:
                        showModal("신규 장소 추가에 실패했습니다.");
                }
                console.error(response);
            }

            props.setNewPlaceAddress(""); // 지도 변경주소 초기화
            props.setAgreeNoneAddress && props.setAgreeNoneAddress(false); // 신규 장소 추가 후 변경주소 초기화
        }
        // 수정 후 정보창이 닫히는 현상으로 삭제한다.
        //props.setActiveIdx(MapToolType.None) // 플로팅버튼 선택 초기화를 하니까 플로팅시트가 안열린다...
        targetXID && props.closeForm(targetXID);
    };

    const rightButton: ButtonProps = {
        label: "저장",
        type: valid && formDataChanged ? MiniButtonType.BLUE : MiniButtonType.DISABLED,
        onClick: valid && formDataChanged ? onSubmit : undefined,
    };

    const leftButton: ButtonProps = {
        label: "이전",
        type: MiniButtonType.GRAY,
        onClick: cancel,
    };

    /**
     * @description 장소 정보(메타데이터) 요청
     */
    const reqData = async () => {
        const mapid = params.get("id")!;
        const res = await DataProvider.GetMetadata({ mapid });
        if (!res.flag) return {} as EditData;

        const result = { ...res.result } as EditData;

        // 수정 로직일 경우 : value 데이터 받아오기
        if (!props.XID || typeof props.XID === "string") return result;

        const POIRes = await DataProvider.GetPlaceInfo({
            mapid,
            XID: props.XID.toString(),
            type: "2",
        });
        result.value = POIRes.flag ? POIRes.result : undefined;

        return result;
    };

    /**
     * @description 장소 정보 MapData type으로 바인딩
     * @param MetaData
     * @return MapData
     */
    const getMapData = async () => {
        const mapData: any = await reqData();
        const metadata = mapData.metadata;
        const labels = { ...metadata.fieldName };
        const value = { ...mapData.value };
        const isValue = props.XID && value;

        const getFieldName = (key: any) => {
            const col = metadata[key];
            return metadata.fieldName[col];
        };

        // const category = isValue ? value[getFieldName("category")] : categories[0].label!;
        const category = categories.length > 0 ? (isValue ? value[getFieldName("category")] : categories[0].label!) : null;

        const reqField = [getFieldName("geoField"), getFieldName("placeName"), getFieldName("category"), getFieldName("imageField")];
        const _data: PlaceData = {
            XID: props.XID ? Number(props.XID) : undefined,
            address: {
                fieldKey: metadata.geoField,
                fieldName: labels[metadata.geoField],
                value: isValue ? value[getFieldName("geoField")] : "",
            },
            category: {
                fieldKey: metadata.category,
                fieldName: labels[metadata.category],
                value: category,
            },
            placeName: {
                fieldKey: metadata.placeName,
                fieldName: labels[metadata.placeName],
                value: isValue ? value[getFieldName("placeName")] : "",
            },
            imageField: metadata.imageField ? { fieldKey: metadata.imageField, fieldName: labels[metadata.imageField], value: [] } : undefined,
            mainData: metadata.mainData
                .filter((key: string) => !reqField.includes(key))
                .filter((key: string) => key !== metadata.category && key !== metadata.geoField && key !== metadata.placeName)
                .map((key: string) => {
                    const _data: Field = {
                        fieldKey: key,
                        fieldName: labels[key],
                        value: isValue ? value[labels[key]] : "",
                    };

                    if (mapData.template) _data.template = setDataTemplate(mapData.template[key]);
                    return _data;
                }),
        };

        if (metadata.extraData) {
            const _extra = metadata.extraData
                .filter((key: string) => !reqField.includes(key))
                .map((key: string) => {
                    const _data: Field = {
                        fieldKey: key,
                        fieldName: labels[key],
                        value: props.XID && mapData.value ? mapData.value[labels[key]] : "",
                    };

                    if (mapData.template) _data.template = setDataTemplate(mapData.template[labels[key]]);
                    return _data;
                });
            _data.extraData = _extra;
        }

        setData(_data);
    };

    const [initialFormSnapshot, setInitialFormSnapshot] = useState<string | null>(null);

    // Form에 set 되는 초기 data값을 직렬화 해서 저장
    useEffect(() => {
        if (data && formRef.current && !initialFormSnapshot) {
            const snapshot = serializeFormData(formRef.current);
            setInitialFormSnapshot(snapshot);
        }
    }, [data]);

    // PlaceForm 컴포넌트 내의 값들을 문자열로 직렬화 하는 함수
    // PlaceForm 컴포넌트의 값이 변경되었는지 비교하는 문자열을 만드는데 사용된다.
    const serializeFormData = (form: HTMLFormElement): string => {
        const formData = new FormData(form);
        const entries: Record<string, any> = {};

        for (const [key, value] of Array.from(formData.entries())) {
            if (entries[key]) {
                if (!Array.isArray(entries[key])) {
                    entries[key] = [entries[key]];
                }
                entries[key].push(value);
            } else {
                entries[key] = value;
            }
        }

        // 키 정렬 → 순서가 달라도 비교 가능하게 만듦
        const sorted = Object.keys(entries)
            .sort()
            .reduce((acc, key) => {
                acc[key] = entries[key];
                return acc;
            }, {} as Record<string, any>);

        return JSON.stringify(sorted); // 문자열로 직렬화
    };

    const setDataTemplate = (template: string) => {
        if (Object.keys(DataTemplate).includes(template)) {
            const key = Object.keys(DataTemplate).indexOf(template);
            return DataTemplate[Object.values(DataTemplate)[key]];
        } else {
            return DataTemplate.none;
        }
    };

    // PlaceForm 컴포넌트 내의 값이 변경될때마다 호출되는 함수
    // 초기 data 값과 비교 하여 변경되었는지 확인한다.
    const handleFormValueChange = () => {
        if (!formRef.current || !initialFormSnapshot) {
            setFormDataChanged(false);
        } else {
            const current = serializeFormData(formRef.current);
            setFormDataChanged(current !== initialFormSnapshot);
            console.log(`initial: ${initialFormSnapshot}`);
            console.log(`current: ${current}`);
        }
    };

    /**
     * 선택된 카테고리 반영
     */
    const setSelectedCategory = (category: string) => {
        data && setData({ ...data, category: { ...data.category, value: category ?? "" } });
        setSubContent(undefined);
    };

    /**
     * 추가한 카테고리 카테고리 목록에 반영
     */
    const addNewCategory = (category: CategoryOptions) => {
        setCategories([...categories, category]);
    };

    /**
     * 카테고리 추가, 선택화면 전환
     */
    // hmchoi isProUser 하드코딩 제거
    const [isProUser, setIsProUser] = useState(false);

    useEffect(() => {
        const checkProUser = async () => {
            const result = await AccountManager.getInstance().isProUser();
            setIsProUser(result);
        }

        checkProUser();
    }, []);

    const getSubContent = () => {
        switch (subContent) {
            case SubContentList.addCategory:
                return <AddCategory setSubContent={setSubContent} categories={categories} addNewCategory={addNewCategory} />;
            case SubContentList.categoryList:
                return (
                    <CategoryList
                        selected={data!.category.value ?? ""}
                        categories={categories}
                        isProUser={isProUser} // @@ TODO: 임시 true값 전달 by YoungJun
                        setSelectedCategory={setSelectedCategory}
                        setSubContent={setSubContent}
                    />
                );
        }
    };

    return (
        <>
            <ModalComponent />
            <div className={`${styles.container} undraggable`}>
                <ContentTitle title={data && data.XID ? "장소 수정" : "신규 장소 등록"} />
                <form
                    className={`${commonStyles.wrapper} ${subContent !== undefined ? styles.hidden : ""}`}
                    ref={formRef}
                    method="POST"
                    encType="multipart/form-data"
                >
                    {data && (
                        <PlaceForm
                            data={data}
                            coords={props.coords}
                            setValid={setValid}
                            setCoords={props.setCoords}
                            setSubContent={setSubContent}
                            toggleAddMode={props.toggleAddMode}
                            newPlaceAddress={props.newPlaceAddress}
                            agreeNoneAddress={props.agreeNoneAddress ? props.agreeNoneAddress : false}
                            setAgreeNoneAddress={props.setAgreeNoneAddress ? props.setAgreeNoneAddress : undefined}
                            setNewPlaceAddress={props.setNewPlaceAddress}
                            revGeoLoading={props.revGeoLoading}
                            onFormValueChange={handleFormValueChange}
                            customModal={props.customModal}
                        />
                    )}
                </form>
                {getSubContent()}
                {subContent === undefined && <BottomButtonBox right={rightButton} left={leftButton} />}
            </div>
        </>
    );
};

export default SinglePlaceFormArea;
